import { Token } from '../../../helpers';

type InvoiceTypeNameType = 'AP' | 'AR';

export interface ExportAllDataInterface {
  detailsData: ExportDataInterface[];
  monthlyData: ExportDataInterface[];
}

export interface ExportDataInterface {
  countryCode: string;
  companyLocalName: string;
  startDatePeriod: string;
  endDatePeriod: string;
  invoiceTypeName: InvoiceTypeNameType;
  materialCode: string;
  materialName: string;
  materialUnitName: string;
  quantity?: number | null;
  volumeTeqCO2?: number | null;
  priceExcludingTax?: number | null;
  pricePerQuantity?: number | null;
  quantityPrevYear?: number | null;
  volumeTeqCO2PrevYear?: number | null;
  priceExcludingTaxPrevYear?: number | null;
  quantityEvolution?: number | null;
  volumeTeqCO2Evolution?: number | null;
  priceExcludingTaxEvolution?: number | null;
}

export interface ExportSerializationResultInterface {
  headers: ExportHeadersInterface;
  data: ExportSerializedDataInterface[];
  detailsData: ExportSerializedDataInterface[];
}

interface ExportHeadersInterface {
  countryCode: string;
  companyLocalName: string;
  startDatePeriod: string;
  endDatePeriod: string;
  invoiceTypeName: string;
  materialCode: string;
  materialName: string;
  materialUnitName: string;
  isService: string;
  quantity: string;
  volumeTeqCO2: string;
  priceExcludingTax: string;
  pricePerQuantity: string;
  quantityPrevYear: string;
  volumeTeqCO2PrevYear: string;
  priceExcludingTaxPrevYear: string;
  quantityEvolution: string;
  volumeTeqCO2Evolution: string;
  priceExcludingTaxEvolution: string;
}

interface ExportSerializedDataInterface {
  countryCode: string;
  companyLocalName: string;
  startDatePeriod: string;
  endDatePeriod: string | null;
  invoiceTypeName: string;
  materialCode: string;
  materialName: string;
  materialUnitName: string;
  quantity?: number | null;
  volumeTeqCO2?: number | null;
  priceExcludingTax?: number | null;
  pricePerQuantity?: number | null;
  quantityPrevYear?: number | null;
  volumeTeqCO2PrevYear?: number | null;
  priceExcludingTaxPrevYear?: number | null;
  quantityEvolution?: number | null;
  volumeTeqCO2Evolution?: number | null;
  priceExcludingTaxEvolution?: number | null;
}

export interface ExportSerializerConstructorInterface {
  translate: (v: string) => string;
}

export class AllSerializer {
  translate: (v: string) => string;

  constructor({ translate }: ExportSerializerConstructorInterface) {
    this.translate = (v: string): string => translate(`export.${v}`);
  }

  public serialize(
    data: ExportAllDataInterface
  ): ExportSerializationResultInterface {
    const monthlyData: ExportDataInterface[] = data.monthlyData;
    const detailsData: ExportDataInterface[] = data.detailsData;
    const monthlyColumns = Object.keys(data.monthlyData[0]) as string[];
    const detailsColumns = Object.keys(data.detailsData[0]) as string[];
    detailsColumns[2] = 'executionDate';
    const columns = [...monthlyColumns, ...detailsColumns, 'isService'].filter(
      (v, i, a) => a.indexOf(v) === i
    );

    const headers = columns.reduce((acc, column) => {
      acc[column] = this.translate('labels.' + column);
      return acc;
    }, {}) as ExportHeadersInterface;

    const serializedDetailsData: ExportSerializedDataInterface[] = [];
    const serializedMonthlyData: ExportSerializedDataInterface[] = [];

    for (let i = 0; i < monthlyData.length; i++) {
      serializedMonthlyData.push(
        columns.reduce((acc, column) => {
          const value = monthlyData[i][column];
          acc[column] = this.mapColumnValue(column, value);
          return acc;
        }, {}) as ExportSerializedDataInterface
      );
    }
    for (let i = 0; i < detailsData.length; i++) {
      serializedDetailsData.push(
        columns.reduce((acc, column) => {
          const value = detailsData[i][column];
          acc[column] = this.mapColumnValue(column, value);
          return acc;
        }, {}) as ExportSerializedDataInterface
      );
    }
    return {
      headers,
      detailsData: serializedDetailsData,
      data: serializedMonthlyData,
    };
  }

  private mapColumnValue(column: string, value: any): any {
    switch (column) {
      case 'startDatePeriod': {
        return new Date(value);
      }
      case 'endDatePeriod': {
        const d = new Date(value);
        d.setHours(d.getHours() - 2);
        return d;
      }
      case 'countryCode':
        return this.translate('countries.' + value);

      case 'invoiceTypeName':
        return this.translate('labels.' + value);

      case 'materialName':
        return value === 'export.labels.others'
          ? this.translate('labels.others')
          : value;

      case 'isService': {
        return this.translate('contracts.valuations');
      }

      case 'priceExcludingTax':
      case 'priceExcludingTaxPrevYear':
        return Math.round((value + Number.EPSILON) * 100) / 100;
      case 'quantity':
      case 'quantityPrevYear':
      case 'volumeTeqCO2':
      case 'volumeTeqCO2PrevYear':
      case 'pricePerQuantity':
        return Math.round((value + Number.EPSILON) * 100) / 100;
      case 'quantityEvolution':
        return value !== null ? value / 100 : '';
      case 'volumeTeqCO2Evolution':
      case 'priceExcludingTaxEvolution':
        return value !== null ? value / 100 : '';

      default:
        return value as string;
    }
  }
}
